import { ModelConstants, TabularCopySink, UpdateMethod, UpdateMethodType } from 'shell-models-lib';
import { IAttribute, IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { OracleTableDataset } from 'app/model/dataset/oracle-table.dataset';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { GlobalVariables } from 'app/services/globals';
import { writeBehaviorOptionValues, writeBehaviorOptionValueTyping } from 'app/shared/constants/write-behavior.const';
import { CopyJobActivity } from 'app/model/copyjob/copy-job.activity';
import { validateUniqueKeyColumns } from 'app/model/pipeline/copysink/copysink.utils';
import { ValueAdapter } from 'app/model/adapters/value.adapter';

@Metamodel.model({
    name: adfModelTypeMap.oracle.copySinkType,
    display: [],
    referable: true,
    tags: { datasetModel: Metamodel.modelFor(OracleTableDataset) }
})
export class OracleSink extends TabularCopySink {
    public static readonly MODEL: IModel;
    public static readonly WRITE_BEHAVIOR: IAttribute;
    public static readonly WRITE_BEHAVIOR_KEYS: IAttribute;

    @Metamodel.attribute({
        templatable: true,
        association: 'WRITE_BATCH_SIZE',
        display: stringResources.ModelName_WriteBatchSize
    })
    public writeBatchSize?: number = 10000;

    @Metamodel.attribute({
        templatable: true,
        association: 'WRITE_BEHAVIOR'
    })
    public writeBehavior?: writeBehaviorOptionValueTyping = GlobalVariables.oracleUpsert
        ? writeBehaviorOptionValues.insert
        : undefined;

    @Metamodel.attribute({
        templatable: true,
        association: 'WRITE_BEHAVIOR_KEYS',
        accepts: ['Array'],
        constraint: {
            validator: OracleSink.validateKeyColumnsMapping
        }
    })
    public writeBehaviorKeys?: string[];

    public get updateMethod() {
        switch (this.writeBehavior) {
            case 'upsert':
                return UpdateMethod.Upsert;
            case 'SCD2':
                return UpdateMethod.SCD2;
            default:
                return UpdateMethod.Append;
        }
    }

    public set updateMethod(value: UpdateMethodType) {
        switch (value) {
            case UpdateMethod.Append:
                this.writeBehavior = 'insert';
                break;
            case UpdateMethod.SCD2:
                this.writeBehavior = 'SCD2';
                break;
            case UpdateMethod.Upsert:
                this.writeBehavior = 'upsert';
                break;
            default:
        }
    }

    public get upsertKeyColumns() {
        return this.writeBehaviorKeys;
    }

    public set upsertKeyColumns(value: string[] | undefined) {
        this.writeBehaviorKeys = value;
    }

    public static validateKeyColumnsMapping(sink: OracleSink, updateMethodLabel?: string) {
        const isCopyJob = sink.owner != null && Metamodel.modelOf(sink.owner).name === ModelConstants.copyJobActivity;
        if (updateMethodLabel == null) {
            if (isCopyJob) {
                updateMethodLabel =
                    (sink.owner as CopyJobActivity).updateMethod === UpdateMethod.SCD2
                        ? stringResources.WriteBehavior_Option_SCD2
                        : stringResources.MergeLabel;
            } else {
                updateMethodLabel = stringResources.WriteBehavior_Option_Upsert;
            }
        }
        const writeBehavior = ValueAdapter.toResource(sink, OracleSink.WRITE_BEHAVIOR);
        if (writeBehavior !== writeBehaviorOptionValues.SCD2 && writeBehavior !== writeBehaviorOptionValues.upsert) {
            // only check for key columns mapping when certain write behavior is upsert or SCD2
            return [];
        }
        return validateUniqueKeyColumns(
            sink,
            undefined,
            sink.writeBehaviorKeys,
            isCopyJob,
            updateMethodLabel
        );
    }
}
